import { AIMessageBubble } from "@/components/ai-assistant/AIMessageBubble";
import { AITypingIndicator } from "@/components/ai-assistant/AITypingIndicator";
import { AISuggestedPrompts } from "@/components/ai-assistant/AISuggestedPrompts";
import { ActionPlanMessageCard } from "@/components/ai/ActionPlanMessageCard";
import { ActionPlanCompletionCard } from "@/components/ai/ActionPlanCompletionCard";
import { DriveDeskAIIcon } from "@/components/icons/DriveDeskAIIcon";

/**
 * The scrollable message area of the AI Office Manager widget: a branded
 * welcome state, message bubbles, thinking indicator, and inline error.
 * Extracted from DriveDeskAIWidget.js so that component stays focused on
 * open/close + composer wiring only.
 *
 * v1.3 Wave 2: messages with `kind: "action_plan"` render as an
 * ActionPlanMessageCard instead of a plain bubble, and
 * `kind: "action_plan_completion"` renders the post-execution summary —
 * both wired to `onApprovePlan`/`onRejectPlan` from `useAIAssistant`.
 */
export const AIChatMessageList = ({
  scrollRef,
  messages,
  suggestions,
  sending,
  isThinking,
  error,
  onPickSuggestion,
  onApprovePlan,
  onRejectPlan,
  actingPlanId,
}) => {
  return (
    <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-4" data-testid="ai-assistant-message-list" aria-live="polite">
      {messages.length === 0 ? (
        // v1.3 "Final Demo Pass" branded welcome — establish the AI
        // Office Manager's value immediately instead of a blank chat box.
        <div className="space-y-5" data-testid="ai-assistant-welcome">
          <div className="flex flex-col items-center text-center gap-3 pt-2 pb-1">
            <DriveDeskAIIcon className="h-16 w-16" size="lg" />
            <div>
              <h3 className="font-heading text-base font-semibold">Your AI Office Manager</h3>
              <p className="text-sm text-muted-foreground mt-1 max-w-xs mx-auto">
                Welcome! I&apos;m here to help you run your business more efficiently.
              </p>
            </div>
          </div>
          <div className="rounded-[var(--radius)] bg-[hsl(var(--champagne))] border border-[hsl(var(--gold)/0.2)] px-4 py-3">
            <p className="text-xs font-medium text-foreground mb-1.5">Ask me about:</p>
            <ul className="text-xs text-muted-foreground space-y-1">
              <li>• Schedules & staffing</li>
              <li>• Students & instructors</li>
              <li>• Business performance & payments</li>
              <li>• Operational recommendations</li>
              <li>• Drafting communications & action plans</li>
            </ul>
          </div>
          <AISuggestedPrompts prompts={suggestions} onPick={onPickSuggestion} disabled={sending} />
        </div>
      ) : (
        messages.map((m) => {
          if (m.kind === "action_plan" && m.plan) {
            return (
              <ActionPlanMessageCard
                key={m.id}
                plan={m.plan}
                onApprove={onApprovePlan}
                onReject={onRejectPlan}
                isActing={actingPlanId === m.plan.id}
              />
            );
          }
          if (m.kind === "action_plan_completion" && m.plan) {
            return <ActionPlanCompletionCard key={m.id} plan={m.plan} />;
          }
          return <AIMessageBubble key={m.id} role={m.role} content={m.content} />;
        })
      )}
      {isThinking && <AITypingIndicator />}
      {error && (
        <p className="text-xs text-destructive" data-testid="ai-assistant-error">{error}</p>
      )}
    </div>
  );
};
